import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import placePledgeLogo from '../assets/place-pledge-logo.svg';
import {
  AUTO_DEACTIVATE_SETTINGS_EVENT,
  loadAutoDeactivateSettings
} from '../utils/autoDeactivateSettings';
import { getCompactAddressLabel } from '../utils/addressLabels';
import { clearDemoPledge, loadDemoPledge } from '../utils/demoPledgeStorage';
import { formatDistance, getDistanceUnit } from '../utils/distanceUnits';
import { isPersonalApp, isPublicApp } from '../utils/appMode';
import {
  deletePublicPledgeTarget,
  loadPublicPledgeTarget
} from '../utils/publicPledgeStorage';

const Page = styled.div`
  min-height: 100vh;
  padding: 2rem 1rem;
  background:
    radial-gradient(circle at 16% 0%, rgba(251, 191, 36, 0.25), transparent 27rem),
    radial-gradient(circle at 92% 8%, rgba(96, 165, 250, 0.38), transparent 28rem),
    linear-gradient(145deg, #101827 0%, #111827 48%, #1f2937 100%);
  color: #f8fafc;

  @media (max-width: 640px) {
    padding: 1.25rem 0.875rem 2rem;
  }
`;

const Shell = styled.div`
  width: min(980px, 100%);
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 620px) {
    flex-direction: column;
  }
`;

const HeaderContent = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 52px;
`;

const BrandLogo = styled.img`
  display: block;
  width: min(300px, 72vw);
  height: auto;

  @media (max-width: 420px) {
    width: min(230px, 58vw);
  }
`;

const Actions = styled.div`
  position: relative;
  flex: 0 0 auto;
  z-index: 20;
`;

const HeaderActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 0.65rem;
`;

const MenuButton = styled.button`
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.14);
  border: 1px solid rgba(251, 191, 36, 0.34);
  border-radius: 999px;
  padding: 0;
  line-height: 1;
  cursor: pointer;

  span {
    width: 17px;
    height: 2.5px;
    background: currentColor;
    border-radius: 999px;
  }

  &:hover {
    background: rgba(251, 191, 36, 0.22);
  }
`;

const HeaderCreateLink = styled(Link)`
  position: relative;
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(251, 191, 36, 0.34);
  border-radius: 999px;
  background: rgba(251, 191, 36, 0.14);
  color: #fbbf24;
  text-decoration: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 18px;
    height: 3px;
    border-radius: 999px;
    background: currentColor;
    transform: translate(-50%, -50%);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(90deg);
  }

  &:hover {
    background: rgba(251, 191, 36, 0.22);
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 0.75rem);
  right: 0;
  z-index: 10;
  display: ${({ $open }) => ($open ? 'grid' : 'none')};
  min-width: 220px;
  max-width: calc(100vw - 1.75rem);
  padding: 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.96);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(18px);
  width: max-content;
`;

const MenuLink = styled(Link)`
  padding: 0.85rem 1rem;
  color: #f8fafc;
  border-radius: 14px;
  font-weight: 900;
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MenuItem = styled.button`
  padding: 0.85rem 1rem;
  color: #f8fafc;
  background: transparent;
  border: 0;
  border-radius: 14px;
  font: inherit;
  font-weight: 900;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Board = styled.main`
  display: grid;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 32px;
  background: rgba(15, 23, 42, 0.72);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(18px);
`;

const PledgeCard = styled.article`
  padding: clamp(1.1rem, 4vw, 1.8rem);
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(59, 130, 246, 0.18), rgba(251, 191, 36, 0.12)),
    rgba(255, 255, 255, 0.06);
`;

const PledgeMapLink = styled.a`
  display: inline-flex;
  width: 100%;
  margin-bottom: 1rem;
  border-radius: 18px;
`;

const PledgeMap = styled.img`
  width: 100%;
  height: clamp(140px, 32vw, 220px);
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 18px;
  object-fit: cover;
  background: rgba(15, 23, 42, 0.72);
`;

const PledgeInfo = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const PledgeTitleRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  padding-inline: 2.75rem;
`;

const PledgeTitleStack = styled.div`
  display: grid;
  justify-items: center;
  gap: 0.35rem;
  min-width: 0;
`;

const PledgeDestination = styled.div`
  color: #f8fafc;
  font-size: clamp(1.55rem, 5vw, 2.7rem);
  font-weight: 950;
  line-height: 1;
  letter-spacing: -0.055em;
  text-align: center;
`;

const PledgeTypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  padding: 0.3rem 0.65rem;
  border: 1px solid rgba(74, 222, 128, 0.3);
  border-radius: 999px;
  background: rgba(22, 101, 52, 0.24);
  color: #bbf7d0;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PledgeDueTime = styled.div`
  color: rgba(226, 232, 240, 0.78);
  font-size: clamp(0.9rem, 3vw, 1.18rem);
  font-weight: 850;
  line-height: 1;
  text-align: center;
`;

const DetailsToggle = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 38px;
  height: 38px;
  min-width: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(251, 191, 36, 0.38);
  border-radius: 999px;
  background: ${({ $open }) => ($open ? 'rgba(251, 191, 36, 0.24)' : 'rgba(15, 23, 42, 0.62)')};
  color: #fbbf24;
  cursor: pointer;
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid currentColor;
  border-radius: 999px;
  font-size: 0.86rem;
  font-weight: 950;
  font-style: normal;
  line-height: 1;

  &::before {
    content: 'i';
  }
`;

const PledgeMetaGrid = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const PledgeMeta = styled.div`
  container-type: inline-size;
  padding: clamp(1rem, 4vw, 1.4rem);
  border: 1px solid rgba(251, 191, 36, 0.28);
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(251, 191, 36, 0.14), rgba(59, 130, 246, 0.08)),
    rgba(15, 23, 42, 0.68);
`;

const PledgeMetaLabel = styled.div`
  color: rgba(203, 213, 225, 0.66);
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PledgeMetaValue = styled.div`
  margin-top: 0.25rem;
  color: ${({ $accent }) => ($accent ? '#fbbf24' : '#f8fafc')};
  font-size: clamp(3rem, 18cqw, 5rem);
  font-weight: 950;
  line-height: 0.92;
  letter-spacing: -0.055em;
  white-space: nowrap;

  @media (max-width: 420px) {
    font-size: clamp(2.85rem, 17.5cqw, 4.6rem);
  }
`;

const FailedLabel = styled.div`
  display: inline-flex;
  align-items: center;
  min-height: 58px;
  margin-top: 0.25rem;
  color: #f87171;
  font-size: clamp(2.35rem, 13cqw, 4rem);
  font-weight: 950;
  line-height: 0.95;
  letter-spacing: 0;
`;

const GuestDemoNote = styled.p`
  display: grid;
  gap: 0.55rem;
  margin: 0.9rem 0 0;
  color: rgba(226, 232, 240, 0.72);
  font-size: 0.92rem;
  font-style: italic;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
`;

const PledgeDetails = styled.div`
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.12);
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.54);
`;

const DetailItem = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const DetailLabel = styled.div`
  color: rgba(203, 213, 225, 0.64);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const DetailValue = styled.div`
  color: #f8fafc;
  font-size: 1rem;
  font-weight: 850;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const DetailActions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 0.25rem;

  > button {
    min-width: 10rem;
  }

  @media (max-width: 520px) {
    > button {
      width: 100%;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(8px);
`;

const DetailsModal = styled.div`
  width: min(480px, 100%);
  max-height: min(82vh, 680px);
  overflow: auto;
  padding:
    clamp(1rem, 4vw, 1.35rem)
    clamp(1rem, 4vw, 1.35rem)
    ${({ $hasStickyActions }) => ($hasStickyActions ? '0' : 'clamp(1rem, 4vw, 1.35rem)')};
  border: 1px solid rgba(251, 191, 36, 0.24);
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(251, 191, 36, 0.13), rgba(59, 130, 246, 0.09)),
    #111827;
  box-shadow: 0 28px 90px rgba(2, 6, 23, 0.55);

  @media (max-width: 520px) {
    max-height: calc(100dvh - 1rem);
    border-radius: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ModalTitleStack = styled.div`
  min-width: 0;
`;

const ModalEyebrow = styled.div`
  color: #fbbf24;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ModalTitle = styled.h2`
  margin: 0.25rem 0 0;
  color: #f8fafc;
  font-size: clamp(1.35rem, 6vw, 2rem);
  font-weight: 950;
  line-height: 1;
  overflow-wrap: anywhere;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.68);
  color: #f8fafc;
  font-size: 1.35rem;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.65rem;
  margin-top: 1.25rem;
`;

const ActionButton = styled.button`
  min-height: 48px;
  border: 0;
  border-radius: 999px;
  padding: 0.75rem 1rem;
  background: ${({ $danger, $primary }) => {
    if ($danger) return 'rgba(248, 113, 113, 0.18)';
    if ($primary) return 'linear-gradient(135deg, #fbbf24, #fb923c)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  color: ${({ $primary }) => ($primary ? '#111827' : '#f8fafc')};
  border: 1px solid ${({ $danger }) => ($danger ? 'rgba(248, 113, 113, 0.32)' : 'rgba(255, 255, 255, 0.12)')};
  font: inherit;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 16px;
  background: ${({ $error }) => ($error ? 'rgba(127, 29, 29, 0.45)' : 'rgba(22, 101, 52, 0.45)')};
  color: ${({ $error }) => ($error ? '#fecaca' : '#bbf7d0')};
  font-weight: 800;
`;

const SuccessGifPanel = styled.div`
  display: grid;
  gap: 0.75rem;
  justify-items: center;
  padding: 1rem;
  border: 1px solid rgba(74, 222, 128, 0.28);
  border-radius: 24px;
  background: rgba(22, 101, 52, 0.22);
`;

const SuccessGif = styled.img`
  display: block;
  width: min(100%, 360px);
  aspect-ratio: 16 / 9;
  border-radius: 18px;
  object-fit: cover;
  background: rgba(15, 23, 42, 0.72);
`;

const EmailPreviewShell = styled.div`
  display: grid;
  gap: 0.85rem;

  @media (max-width: 520px) {
    gap: 0.65rem;
  }
`;

const EmailPreviewMeta = styled.div`
  display: grid;
  gap: 0.35rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.62);
  color: #cbd5e1;
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;

  strong {
    color: #f8fafc;
  }
`;

const EmailPreviewCard = styled.div`
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  color: #111827;
`;

const EmailPreviewHeader = styled.div`
  padding: 1.25rem 1.15rem 0.8rem;

  @media (max-width: 520px) {
    padding: 1rem 1rem 0.55rem;
  }
`;

const EmailPreviewBrand = styled.div`
  color: #9a6700;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const EmailPreviewHeadline = styled.h3`
  margin: 0.55rem 0 0;
  color: #111827;
  font-size: clamp(1.35rem, 5vw, 1.8rem);
  font-weight: 950;
  line-height: 1.16;

  @media (max-width: 520px) {
    font-size: 1.45rem;
  }
`;

const EmailPreviewBody = styled.div`
  display: grid;
  gap: 1rem;
  padding: 0 1.15rem 1.25rem;
  color: #4b5563;
  font-size: 0.96rem;
  line-height: 1.55;

  p {
    margin: 0;
  }

  @media (max-width: 520px) {
    gap: 0.75rem;
    padding: 0 1rem 1rem;
    font-size: 0.92rem;
    line-height: 1.45;
  }
`;

const EmailPreviewBlock = styled.div`
  display: grid;
  gap: 0.35rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 520px) {
    padding-bottom: 0.75rem;
  }
`;

const EmailPreviewLabel = styled.div`
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const EmailPreviewPrimary = styled.div`
  color: #111827;
  font-size: 1.08rem;
  font-weight: 900;
  line-height: 1.45;
`;

const EmailPreviewMessage = styled.div`
  color: #111827;
  font-size: clamp(1.2rem, 4.5vw, 1.5rem);
  font-weight: 950;
  line-height: 1.35;
  white-space: pre-wrap;

  @media (max-width: 520px) {
    font-size: 1.2rem;
  }
`;

const EmailPreviewFooter = styled.div`
  padding: 0.85rem 1.15rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.78rem;
  font-style: italic;
  line-height: 1.45;

  @media (max-width: 520px) {
    padding: 0.7rem 1rem;
    font-size: 0.72rem;
  }
`;

const ModalActions = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  margin-top: 0.85rem;
  padding: 0.75rem 0 1rem;
  background: linear-gradient(to bottom, rgba(17, 24, 39, 0.72), #111827 28%);

  @media (max-width: 520px) {
    padding-bottom: 0.75rem;
  }

  > button {
    min-width: 8rem;
  }

  @media (max-width: 520px) {
    > button {
      width: 100%;
    }
  }
`;

const EmptyState = styled.div`
  padding: 2rem;
  border: 1px dashed rgba(226, 232, 240, 0.28);
  border-radius: 24px;
  color: #cbd5e1;
  text-align: center;

  h2 {
    margin: 0 0 1.15rem;
    color: rgba(226, 232, 240, 0.78);
    font-size: clamp(1.35rem, 3vw, 2rem);
    font-weight: 500;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const LoadingPledgesState = styled(EmptyState)`
  display: grid;
  place-items: center;
  min-height: 132px;
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 4px solid rgba(226, 232, 240, 0.18);
  border-top-color: #fbbf24;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const getApiUrl = () => {
  return window.RUNTIME_CONFIG?.apiUrl || process.env.REACT_APP_API_URL;
};

const getDismissedFailedPledgesStorageKey = (user) => {
  return `placepledge_dismissed_failed_pledges_${user?.uid || user?.email || 'current'}`;
};

const loadDismissedFailedPledgeIds = (storageKey) => {
  try {
    const rawValue = window.localStorage.getItem(storageKey);
    const ids = JSON.parse(rawValue || '[]');

    return new Set(Array.isArray(ids) ? ids : []);
  } catch (error) {
    return new Set();
  }
};

const saveDismissedFailedPledgeIds = (storageKey, ids) => {
  window.localStorage.setItem(
    storageKey,
    JSON.stringify([...ids])
  );
};

const formatTime = (time) => {
  return new Date(time).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatRelativeDateLabel = (time) => {
  const dateValue = new Date(time);

  if (Number.isNaN(dateValue.getTime())) {
    return '';
  }

  const pledgeDate = new Date(dateValue);
  pledgeDate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (pledgeDate.getTime() === today.getTime()) {
    return 'Today';
  }

  if (pledgeDate.getTime() === tomorrow.getTime()) {
    return 'TMRW';
  }

  return dateValue.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: pledgeDate.getFullYear() === today.getFullYear() ? undefined : 'numeric'
  });
};

const formatPledgeDueTime = (time) => {
  const dateLabel = formatRelativeDateLabel(time);
  const timeLabel = formatTime(time).replace(/\s/g, '').toLowerCase();

  return dateLabel ? `${dateLabel} @ ${timeLabel}` : `@ ${timeLabel}`;
};

const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

const formatPunishmentType = (alarm) => {
  if (alarm?.punishmentType === 'email') {
    return 'Email';
  }

  if (alarm?.punishmentType === 'monetary') {
    return 'Money';
  }

  return 'Consequence';
};

const getGuestSenderName = (alarm, user) => {
  return alarm?.metadata?.guestSenderName || user?.displayName || user?.email || 'Someone';
};

const isPledgeFailed = (alarm, currentTime) => {
  if (alarm?.failed) {
    return true;
  }

  const dueAt = new Date(alarm?.time).getTime();

  return !Number.isNaN(dueAt) && dueAt <= currentTime;
};

const formatCountdown = (time, currentTime) => {
  const remainingMs = new Date(time).getTime() - currentTime;

  if (Number.isNaN(remainingMs)) {
    return 'Unknown';
  }

  if (remainingMs <= 0) {
    return 'Due now';
  }

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }

  if (hours > 0 || days > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0 || hours > 0 || days > 0) {
    parts.push(`${minutes}m`);
  }

  parts.push(`${seconds}s`);

  return parts.join(' ');
};

const SUCCESS_GIF_TAG = 'success celebration';
const MAX_FAILED_PLEDGES_ON_DASHBOARD = 3;

const formatContactList = (emails = []) => {
  if (!Array.isArray(emails) || emails.length === 0) {
    return 'No contact selected';
  }

  return emails.join(', ');
};

const isRunPledge = (alarm) => alarm?.pledgeType === 'run';

const formatRunDistance = (alarm) => {
  const miles = Number(alarm?.metadata?.distanceMiles);

  if (!Number.isFinite(miles)) {
    return 'Run pledge';
  }

  return `Run ${miles.toFixed(2)} mi`;
};

const formatRunPace = (alarm) => {
  const pace = Number(alarm?.metadata?.paceMinutesPerMile);

  if (!Number.isFinite(pace)) {
    return 'Not set';
  }

  return `${pace.toFixed(pace % 1 === 0 ? 0 : 1)} min/mi`;
};

const formatRunTimer = (alarm) => {
  const minutes = Number(alarm?.metadata?.timerMinutes);

  if (!Number.isFinite(minutes)) {
    return 'Not set';
  }

  return `${minutes.toFixed(minutes % 1 === 0 ? 0 : 1)} min`;
};

const formatRunDistanceSource = (alarm) => {
  return alarm?.metadata?.distanceSource === 'walking_route'
    ? 'Walking route'
    : 'Straight line';
};

const getGiphyApiKey = () => {
  return window.RUNTIME_CONFIG?.giphy?.apiKey || process.env.REACT_APP_GIPHY_API_KEY || '';
};

const getRandomSuccessGif = async () => {
  const apiKey = getGiphyApiKey();

  if (!apiKey) {
    return '';
  }

  const response = await axios.get('https://api.giphy.com/v1/gifs/random', {
    params: {
      api_key: apiKey,
      tag: SUCCESS_GIF_TAG,
      rating: 'g'
    }
  });
  const gif = response.data?.data;

  return (
    gif?.images?.downsized_medium?.url ||
    gif?.images?.fixed_height?.url ||
    gif?.image_url ||
    ''
  );
};

const getLocationPosition = (location) => {
  if (!location) {
    return null;
  }

  if (location.location) {
    return location.location;
  }

  return {
    lat: location.lat,
    lng: location.lng
  };
};

const getStaticMapUrl = (location, apiKey) => {
  const position = getLocationPosition(location);

  if (!apiKey || position?.lat === undefined || position?.lng === undefined) {
    return null;
  }

  const marker = encodeURIComponent(`color:orange|${position.lat},${position.lng}`);

  return `https://maps.googleapis.com/maps/api/staticmap?center=${position.lat},${position.lng}&zoom=16&size=288x192&scale=2&maptype=roadmap&markers=${marker}&key=${apiKey}`;
};

const getMapsUrl = (location) => {
  const position = getLocationPosition(location);

  if (position?.lat === undefined || position?.lng === undefined) {
    return null;
  }

  const query = encodeURIComponent(`${position.lat},${position.lng}`);

  return `https://maps.apple.com/?ll=${query}&q=${encodeURIComponent(location.name || query)}`;
};

const getDistanceInFeet = (firstPosition, secondPosition) => {
  if (
    firstPosition?.lat === undefined ||
    firstPosition?.lng === undefined ||
    secondPosition?.lat === undefined ||
    secondPosition?.lng === undefined
  ) {
    return null;
  }

  const earthRadiusFeet = 20902231;
  const toRadians = (degrees) => degrees * Math.PI / 180;
  const firstLat = toRadians(firstPosition.lat);
  const secondLat = toRadians(secondPosition.lat);
  const latDelta = toRadians(secondPosition.lat - firstPosition.lat);
  const lngDelta = toRadians(secondPosition.lng - firstPosition.lng);
  const haversineValue =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(firstLat) * Math.cos(secondLat) * Math.sin(lngDelta / 2) ** 2;
  const angularDistance = 2 * Math.atan2(Math.sqrt(haversineValue), Math.sqrt(1 - haversineValue));

  return Math.round(earthRadiusFeet * angularDistance);
};

const formatDistanceFromFeet = (distanceInFeet, unit = 'feet') => {
  const distanceUnit = getDistanceUnit(unit);

  return formatDistance(distanceInFeet / distanceUnit.feetMultiplier, unit);
};

const getFreshLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is unavailable'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      reject,
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000
      }
    );
  });
};

const isAlarmWithinAutoDeactivateWindow = (alarm, windowMinutes) => {
  const alarmTime = new Date(alarm.time).getTime();

  if (Number.isNaN(alarmTime)) {
    return false;
  }

  return Math.abs(alarmTime - Date.now()) <= windowMinutes * 60 * 1000;
};

const getDemoEmailNotice = (failed) => {
  return failed
    ? '*No email has been sent as this is a demo.'
    : '*No email will be sent as this is a demo.';
};

const getDemoEmailContext = (failed) => {
  return failed
    ? 'In a real pledge, your recipient would have received your message when the timer expired.'
    : 'Physically move your phone to the GPS location above before the timer expires.';
};

const Dashboard = ({ isDemo = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [alarms, setAlarms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deactivating, setDeactivating] = useState({});
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [successGif, setSuccessGif] = useState('');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [detailsPledgeId, setDetailsPledgeId] = useState(null);
  const [previewPledgeId, setPreviewPledgeId] = useState(null);
  const dismissedFailedPledgesStorageKey = getDismissedFailedPledgesStorageKey(user);
  const [dismissedFailedPledgeIds, setDismissedFailedPledgeIds] = useState(() => loadDismissedFailedPledgeIds(dismissedFailedPledgesStorageKey));
  const [autoDeactivateSettings, setAutoDeactivateSettings] = useState(loadAutoDeactivateSettings);
  const autoDeactivateInFlightRef = useRef(new Set());
  const successTimerRef = useRef(null);
  const googleMapsApiKey = window.RUNTIME_CONFIG?.googleMaps?.apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const publicMode = isPublicApp();
  const canUsePersonalFeatures = isPersonalApp();
  const canUsePersonalAdminFeatures = canUsePersonalFeatures && user?.isAdmin === true;

  const visibleAlarms = useMemo(() => {
    const activeVisibleAlarms = alarms
      .filter(alarm => alarm.active && !isPledgeFailed(alarm, currentTime))
      .sort((firstAlarm, secondAlarm) => new Date(firstAlarm.time) - new Date(secondAlarm.time));

    const failedVisibleAlarms = alarms
      .filter(alarm => isPledgeFailed(alarm, currentTime) && !dismissedFailedPledgeIds.has(alarm.id))
      .sort((firstAlarm, secondAlarm) => new Date(secondAlarm.time) - new Date(firstAlarm.time))
      .slice(0, MAX_FAILED_PLEDGES_ON_DASHBOARD);

    return [...activeVisibleAlarms, ...failedVisibleAlarms];
  }, [alarms, currentTime, dismissedFailedPledgeIds]);

  const locationNameById = useMemo(() => {
    return locations.reduce((result, location) => ({
      ...result,
      [location.id]: getCompactAddressLabel(location.name)
    }), {});
  }, [locations]);

  const locationById = useMemo(() => {
    return locations.reduce((result, location) => ({
      ...result,
      [location.id]: location
    }), {});
  }, [locations]);

  const detailsPledge = useMemo(() => {
    return visibleAlarms.find(alarm => alarm.id === detailsPledgeId) || null;
  }, [visibleAlarms, detailsPledgeId]);
  const previewPledge = useMemo(() => {
    return visibleAlarms.find(alarm => alarm.id === previewPledgeId) || null;
  }, [visibleAlarms, previewPledgeId]);
  const previewPledgeEmail = useMemo(() => {
    if (!previewPledge) {
      return null;
    }

    const senderName = getGuestSenderName(previewPledge, user);
    const locationName = previewPledge.locationLabel || locationNameById[previewPledge.locationId] || locationById[previewPledge.locationId]?.name || 'your pledge destination';

    return {
      to: formatContactList(previewPledge.contactEmails),
      cc: user?.email || 'you@example.com',
      subject: `${senderName} missed a PlacePledge`,
      senderName,
      locationName,
      dueAt: formatPledgeDueTime(previewPledge.time),
      message: previewPledge.shameMessage || 'No message set'
    };
  }, [locationById, locationNameById, previewPledge, user]);
  const isGuestUser = user?.isAnonymous === true;

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isDemo) {
      const demoPledge = loadDemoPledge();

      if (demoPledge) {
        setLocations([demoPledge.location]);
        setAlarms([demoPledge.alarm]);
      }

      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const idToken = await user.getIdToken();
        const [activeAlarmsResponse, failedAlarmsResponse, locationsResponse] = await Promise.all([
          axios.get(`${getApiUrl()}/alarms`, {
            params: {
              active: true
            },
            headers: { Authorization: `Bearer ${idToken}` }
          }),
          axios.get(`${getApiUrl()}/alarms`, {
            params: {
              failed: true,
              limit: MAX_FAILED_PLEDGES_ON_DASHBOARD
            },
            headers: { Authorization: `Bearer ${idToken}` }
          }),
          publicMode
            ? Promise.resolve({ data: [] })
            : axios.get(`${getApiUrl()}/locations`, {
              headers: { Authorization: `Bearer ${idToken}` }
            })
        ]);

        const activeAlarms = activeAlarmsResponse.data.items || activeAlarmsResponse.data;
        const failedAlarms = failedAlarmsResponse.data.items || failedAlarmsResponse.data;
        const alarmsById = [...activeAlarms, ...failedAlarms].reduce((result, alarm) => ({
          ...result,
          [alarm.id]: alarm
        }), {});

        setAlarms(Object.values(alarmsById));
        setLocations(locationsResponse.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, isDemo, publicMode]);

  useEffect(() => {
    setDismissedFailedPledgeIds(loadDismissedFailedPledgeIds(dismissedFailedPledgesStorageKey));
  }, [dismissedFailedPledgesStorageKey]);

  useEffect(() => {
    const reloadSettings = () => {
      setAutoDeactivateSettings(loadAutoDeactivateSettings());
    };

    window.addEventListener('storage', reloadSettings);
    window.addEventListener(AUTO_DEACTIVATE_SETTINGS_EVENT, reloadSettings);

    return () => {
      window.removeEventListener('storage', reloadSettings);
      window.removeEventListener(AUTO_DEACTIVATE_SETTINGS_EVENT, reloadSettings);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!detailsPledgeId) {
      return undefined;
    }

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setDetailsPledgeId(null);
      }
    };

    window.addEventListener('keydown', closeOnEscape);

    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [detailsPledgeId]);

  const openPledgeDetails = (pledgeId) => {
    setDetailsPledgeId(pledgeId);
  };

  const handleDismissFailedPledge = (pledgeId) => {
    setDismissedFailedPledgeIds(prev => {
      const next = new Set(prev);

      next.add(pledgeId);
      saveDismissedFailedPledgeIds(dismissedFailedPledgesStorageKey, next);

      return next;
    });
    setDetailsPledgeId(currentId => (currentId === pledgeId ? null : currentId));
    setErrors(prev => ({ ...prev, [pledgeId]: null }));
  };

  const renderPledgeDetails = (alarm) => {
    const runPledge = isRunPledge(alarm);

    return (
      <PledgeDetails>
        {runPledge && (
          <>
            <DetailItem>
              <DetailLabel>Pace</DetailLabel>
              <DetailValue>{formatRunPace(alarm)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Time limit</DetailLabel>
              <DetailValue>{formatRunTimer(alarm)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Distance</DetailLabel>
              <DetailValue>{formatRunDistanceSource(alarm)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Destination</DetailLabel>
              <DetailValue>{locationNameById[alarm.locationId] || 'Temporary run pin'}</DetailValue>
            </DetailItem>
          </>
        )}
        {alarm.punishmentType === 'email' ? (
          <>
            <DetailItem>
              <DetailLabel>Email to</DetailLabel>
              <DetailValue>{formatContactList(alarm.contactEmails)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Message</DetailLabel>
              <DetailValue>{alarm.shameMessage || 'No message set'}</DetailValue>
            </DetailItem>
            {isGuestUser && (
              <DetailActions>
                <ActionButton
                  type="button"
                  onClick={() => {
                    setDetailsPledgeId(null);
                    setPreviewPledgeId(alarm.id);
                  }}
                >
                  Preview email
                </ActionButton>
              </DetailActions>
            )}
          </>
        ) : (
          <DetailItem>
            <DetailLabel>Cost if failed</DetailLabel>
            <DetailValue>{formatMoney(alarm.amount)}</DetailValue>
          </DetailItem>
        )}
      </PledgeDetails>
    );
  };

  const showSuccess = async (message) => {
    if (successTimerRef.current) {
      window.clearTimeout(successTimerRef.current);
    }

    setSuccessMessage(message);
    setSuccessGif('');

    successTimerRef.current = window.setTimeout(() => {
      setSuccessMessage('');
      setSuccessGif('');
      successTimerRef.current = null;
    }, 5000);

    try {
      const gifUrl = await getRandomSuccessGif();

      if (gifUrl && successTimerRef.current) {
        setSuccessGif(gifUrl);
      }
    } catch (error) {
      console.error('Error fetching success GIF:', error);
    }
  };

  const handleDeactivate = useCallback(async (alarmId, options = {}) => {
    const { automatic = false } = options;

    if (autoDeactivateInFlightRef.current.has(alarmId)) {
      return;
    }

    autoDeactivateInFlightRef.current.add(alarmId);

    if (!automatic) {
      setDeactivating(prev => ({ ...prev, [alarmId]: true }));
      setErrors(prev => ({ ...prev, [alarmId]: null }));
    }

    try {
      const alarm = alarms.find(currentAlarm => currentAlarm.id === alarmId);

      if (publicMode && alarm?.verificationMode === 'client') {
        const publicTarget = loadPublicPledgeTarget(alarmId);

        if (!publicTarget) {
          setErrors(prev => ({
            ...prev,
            [alarmId]: 'This pledge cannot be verified on this device because its private destination is missing.'
          }));
          return;
        }

        const freshLocation = await getFreshLocation();
        const targetLocation = {
          lat: publicTarget.targetLat,
          lng: publicTarget.targetLng
        };
        const distanceInFeet = getDistanceInFeet(freshLocation, targetLocation);
        const requiredDistance = Number(publicTarget.radiusFeet || alarm.requiredDistance || 200);
        const distanceUnit = publicTarget.radiusUnit || alarm.requiredDistanceUnit || 'feet';

        if (distanceInFeet === null || distanceInFeet > requiredDistance) {
          if (!automatic) {
            setErrors(prev => ({
              ...prev,
              [alarmId]: distanceInFeet === null
                ? 'Unable to compare your location with the private pledge destination.'
                : `You need to be closer to fulfill this pledge. Currently ${formatDistanceFromFeet(distanceInFeet, distanceUnit)} away, need to be within ${formatDistanceFromFeet(requiredDistance, distanceUnit)}.`
            }));
          }
          return;
        }

        const response = await axios.post(
          `${getApiUrl()}/alarms/deactivate`,
          {
            alarmId,
            fulfillmentSecret: publicTarget.fulfillmentSecret
          },
          {
            headers: { Authorization: `Bearer ${await user.getIdToken()}` }
          }
        );

        if (response.data.success) {
          deletePublicPledgeTarget(alarmId);
          setAlarms(prev => prev.filter(currentAlarm => currentAlarm.id !== alarmId));
          showSuccess('Pledge fulfilled. Nice work.');
        } else if (!automatic) {
          setErrors(prev => ({
            ...prev,
            [alarmId]: response.data.message
          }));
        }

        return;
      }

      const freshLocation = await getFreshLocation();

      if (isDemo) {
        const pledgeLocation = locationById[alarm?.locationId];
        const targetLocation = getLocationPosition(pledgeLocation);
        const distanceInFeet = getDistanceInFeet(freshLocation, targetLocation);
        const requiredDistance = Number(pledgeLocation?.requiredDistance || 200);
        const distanceUnit = pledgeLocation?.requiredDistanceUnit || 'feet';

        if (distanceInFeet !== null && distanceInFeet <= requiredDistance) {
          clearDemoPledge();
          setAlarms([]);
          showSuccess('Demo pledge fulfilled. Nice work.');
        } else if (!automatic) {
          setErrors(prev => ({
            ...prev,
            [alarmId]: distanceInFeet === null
              ? 'Unable to compare your location with the pledge destination. Try dropping the pin again.'
              : `You need to be closer to fulfill this pledge. Currently ${formatDistanceFromFeet(distanceInFeet, distanceUnit)} away, need to be within ${formatDistanceFromFeet(requiredDistance, distanceUnit)}.`
          }));
        }

        return;
      }

      const response = await axios.post(
        `${getApiUrl()}/alarms/deactivate`,
        {
          alarmId,
          currentLocation: freshLocation
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      if (response.data.success) {
        setAlarms(prev => prev.filter(alarm => alarm.id !== alarmId));
        showSuccess('Pledge fulfilled. Nice work.');
      } else if (!automatic) {
        setErrors(prev => ({
          ...prev,
          [alarmId]: response.data.message
        }));
      }
    } catch (error) {
      if (!automatic) {
        setErrors(prev => ({
          ...prev,
          [alarmId]: error.response?.data?.message || 'Unable to get a fresh location. Please enable location services and try again.'
        }));
      }
    } finally {
      autoDeactivateInFlightRef.current.delete(alarmId);

      if (!automatic) {
        setDeactivating(prev => ({ ...prev, [alarmId]: false }));
      }
    }
  }, [alarms, locationById, user, isDemo, publicMode]);

  useEffect(() => {
    if (isDemo || !canUsePersonalFeatures || !autoDeactivateSettings.enabled || loading) {
      return undefined;
    }

    const tryAutoDeactivate = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      alarms
        .filter(alarm => alarm.active && !isPledgeFailed(alarm, Date.now()))
        .filter(alarm => isAlarmWithinAutoDeactivateWindow(alarm, autoDeactivateSettings.windowMinutes))
        .forEach(alarm => {
          handleDeactivate(alarm.id, { automatic: true });
        });
    };

    tryAutoDeactivate();
    const intervalId = window.setInterval(
      tryAutoDeactivate,
      autoDeactivateSettings.intervalSeconds * 1000
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    alarms,
    autoDeactivateSettings.enabled,
    autoDeactivateSettings.intervalSeconds,
    autoDeactivateSettings.windowMinutes,
    handleDeactivate,
    isDemo,
    loading,
    canUsePersonalFeatures
  ]);

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  const handleGetPassword = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/password`, {
        headers: { Authorization: `Bearer ${await user.getIdToken()}` }
      });

      window.alert(response.data.password);
    } catch (error) {
      window.alert(error.response?.data?.message || 'Failed to retrieve password');
    } finally {
      setIsMenuOpen(false);
    }
  };

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderContent>
            <BrandRow>
              <BrandLogo src={placePledgeLogo} alt="Place Pledge" />
              <HeaderActions>
                <Actions>
                  <MenuButton
                    type="button"
                    aria-label="Open menu"
                    aria-expanded={isMenuOpen}
                    onClick={() => setIsMenuOpen(prev => !prev)}
                  >
                    <span />
                    <span />
                    <span />
                  </MenuButton>
                  <Menu $open={isMenuOpen}>
                    <MenuLink to="/faq" onClick={() => setIsMenuOpen(false)}>
                      FAQ
                    </MenuLink>
                    <MenuLink to="/settings" onClick={() => setIsMenuOpen(false)}>
                      Settings
                    </MenuLink>
                    <MenuLink to="/pledge-history" onClick={() => setIsMenuOpen(false)}>
                      Pledge History
                    </MenuLink>
                    {!isDemo && canUsePersonalAdminFeatures && (
                      <MenuLink to="/run-pledge" onClick={() => setIsMenuOpen(false)}>
                        Run Pledge
                      </MenuLink>
                    )}
                    {!isDemo && canUsePersonalAdminFeatures && (
                      <MenuItem type="button" onClick={handleGetPassword}>
                        Get admin password
                      </MenuItem>
                    )}
                    {isGuestUser ? (
                      <MenuItem type="button" onClick={handleSignOut}>
                        Sign out
                      </MenuItem>
                    ) : isDemo ? (
                      <MenuLink to="/login" onClick={() => setIsMenuOpen(false)}>
                        Sign in
                      </MenuLink>
                    ) : (
                      <MenuItem type="button" onClick={handleSignOut}>
                        Sign out
                      </MenuItem>
                    )}
                  </Menu>
                </Actions>
                <HeaderCreateLink to={isDemo ? '/demo' : '/create-alarm'} aria-label="New Pledge" />
              </HeaderActions>
            </BrandRow>
          </HeaderContent>
        </Header>

        <Board>
          {successMessage && (
            <SuccessGifPanel role="status" aria-live="polite">
              <Message>{successMessage}</Message>
              {successGif && <SuccessGif src={successGif} alt="" />}
            </SuccessGifPanel>
          )}
          {loading ? (
            <LoadingPledgesState role="status" aria-label="Loading pledges">
              <LoadingSpinner />
            </LoadingPledgesState>
          ) : visibleAlarms.length === 0 ? (
            <EmptyState>
              <h2>No active pledges.</h2>
            </EmptyState>
          ) : (
            visibleAlarms.map((alarm) => {
              const pledgeLocation = locationById[alarm.locationId];
              const staticMapUrl = getStaticMapUrl(pledgeLocation, googleMapsApiKey);
              const mapsUrl = getMapsUrl(pledgeLocation);
              const runPledge = isRunPledge(alarm);
              const failed = isPledgeFailed(alarm, currentTime);
              const pledgeName = runPledge
                ? formatRunDistance(alarm)
                : alarm.locationLabel || locationNameById[alarm.locationId] || 'Saved place';

              return (
                <PledgeCard key={alarm.id}>
                  <PledgeTitleRow>
                    <PledgeTitleStack>
                      {runPledge && <PledgeTypeBadge>Run</PledgeTypeBadge>}
                      <PledgeDestination>{pledgeName}</PledgeDestination>
                      <PledgeDueTime>{formatPledgeDueTime(alarm.time)}</PledgeDueTime>
                    </PledgeTitleStack>
                    <DetailsToggle
                      type="button"
                      $open={detailsPledgeId === alarm.id}
                      aria-label="View pledge details"
                      aria-haspopup="dialog"
                      onClick={() => openPledgeDetails(alarm.id)}
                    >
                      <InfoIcon aria-hidden="true" />
                    </DetailsToggle>
                  </PledgeTitleRow>
                  {staticMapUrl && mapsUrl && (
                    <PledgeMapLink href={mapsUrl} target="_blank" rel="noreferrer" aria-label={`Open ${pledgeLocation?.name || 'pledge location'} in maps`}>
                      <PledgeMap src={staticMapUrl} alt="" loading="lazy" />
                    </PledgeMapLink>
                  )}
                  <PledgeInfo>
                    <PledgeMetaGrid>
                      <PledgeMeta>
                        <PledgeMetaLabel>{failed ? 'Status' : 'Remaining'}</PledgeMetaLabel>
                        {failed ? (
                          <FailedLabel>FAILED</FailedLabel>
                        ) : (
                          <PledgeMetaValue $accent>{formatCountdown(alarm.time, currentTime)}</PledgeMetaValue>
                        )}
                      </PledgeMeta>
                    </PledgeMetaGrid>
                  </PledgeInfo>

                  {isGuestUser && alarm.punishmentType === 'email' && (
                    <GuestDemoNote>
                      <span>{getDemoEmailContext(failed)}</span>
                      <span>{getDemoEmailNotice(failed)}</span>
                    </GuestDemoNote>
                  )}

                  <CardActions>
                    <ActionButton
                      $primary={!failed}
                      type="button"
                      onClick={() => {
                        if (failed) {
                          handleDismissFailedPledge(alarm.id);
                          return;
                        }

                        handleDeactivate(alarm.id);
                      }}
                      disabled={!failed && deactivating[alarm.id]}
                    >
                      {failed ? 'Dismiss' : deactivating[alarm.id] ? 'Checking...' : 'Fulfill pledge'}
                    </ActionButton>
                    {isGuestUser && alarm.punishmentType === 'email' && (
                      <ActionButton
                        $primary={failed}
                        type="button"
                        onClick={() => navigate('/login')}
                      >
                        Sign up
                      </ActionButton>
                    )}
                  </CardActions>

                  {errors[alarm.id] && <Message $error>{errors[alarm.id]}</Message>}
                </PledgeCard>
              );
            })
          )}
        </Board>

        {detailsPledge && (
          <ModalOverlay
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setDetailsPledgeId(null);
              }
            }}
          >
            <DetailsModal role="dialog" aria-modal="true" aria-labelledby="pledge-details-title">
              <ModalHeader>
                <ModalTitleStack>
                  <ModalEyebrow>Consequence</ModalEyebrow>
                  <ModalTitle id="pledge-details-title">{formatPunishmentType(detailsPledge)}</ModalTitle>
                </ModalTitleStack>
                <CloseButton
                  type="button"
                  aria-label="Close pledge details"
                  onClick={() => setDetailsPledgeId(null)}
                >
                  &times;
                </CloseButton>
              </ModalHeader>
              {renderPledgeDetails(detailsPledge)}
            </DetailsModal>
          </ModalOverlay>
        )}

        {previewPledgeEmail && (
          <ModalOverlay
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setPreviewPledgeId(null);
              }
            }}
          >
            <DetailsModal $hasStickyActions role="dialog" aria-modal="true" aria-labelledby="email-preview-title">
              <ModalHeader>
                <ModalTitleStack>
                  <ModalEyebrow>Demo email</ModalEyebrow>
                  <ModalTitle id="email-preview-title">Preview</ModalTitle>
                </ModalTitleStack>
                <CloseButton
                  type="button"
                  aria-label="Close email preview"
                  onClick={() => setPreviewPledgeId(null)}
                >
                  &times;
                </CloseButton>
              </ModalHeader>
              <EmailPreviewShell>
                <EmailPreviewMeta>
                  <div><strong>To:</strong> {previewPledgeEmail.to}</div>
                  <div><strong>Cc:</strong> {previewPledgeEmail.cc}</div>
                  <div><strong>Subject:</strong> {previewPledgeEmail.subject}</div>
                </EmailPreviewMeta>
                <EmailPreviewCard>
                  <EmailPreviewHeader>
                    <EmailPreviewBrand>PlacePledge</EmailPreviewBrand>
                    <EmailPreviewHeadline>{previewPledgeEmail.senderName} missed a PlacePledge</EmailPreviewHeadline>
                  </EmailPreviewHeader>
                  <EmailPreviewBody>
                    <p>They precommitted to send this accountability note if they missed their pledge.</p>
                    <EmailPreviewBlock>
                      <EmailPreviewLabel>The pledge</EmailPreviewLabel>
                      <EmailPreviewPrimary>Be at {previewPledgeEmail.locationName}</EmailPreviewPrimary>
                      <p>by {previewPledgeEmail.dueAt}</p>
                    </EmailPreviewBlock>
                    <EmailPreviewBlock>
                      <EmailPreviewLabel>Their message</EmailPreviewLabel>
                      <EmailPreviewMessage>{previewPledgeEmail.message}</EmailPreviewMessage>
                    </EmailPreviewBlock>
                    <p>PlacePledge helps people make location-based promises with real consequences.</p>
                  </EmailPreviewBody>
                  <EmailPreviewFooter>{getDemoEmailNotice(isPledgeFailed(previewPledge, currentTime))}</EmailPreviewFooter>
                </EmailPreviewCard>
              </EmailPreviewShell>
              <ModalActions>
                <ActionButton
                  type="button"
                  onClick={() => setPreviewPledgeId(null)}
                >
                  OK
                </ActionButton>
              </ModalActions>
            </DetailsModal>
          </ModalOverlay>
        )}

      </Shell>
    </Page>
  );
};

export default Dashboard;
